package com.vamvisions.main.pages.home

import com.vamvisions.main.theme.NARROW_SCREEN
import com.vamvisions.main.theme.REDUCED_MOTION_QUERY
import com.vamvisions.main.theme.REVEAL_GATE_CLASS
import com.vamvisions.main.theme.Tokens
import com.vamvisions.main.theme.cssVar
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.media
import org.jetbrains.compose.web.css.percent

private const val BUTTON_SHADOW_COLOR = "color-mix(in srgb, var(${Tokens.COLOR_BRAND}) 25%, transparent)"

/**
 * The Home page: the greeting, the three highlight cards and their reveal sequence. Timings come from the Figma motion
 * keyframes. Each animation fills backwards only, so once it ends the element's own styles (hover, shadow) apply again.
 */
object HomeStyles : StyleSheet() {

    private val rise16 by keyframes {
        from {
            property("opacity", "0")
            property("transform", "translateY(16px)")
        }
        to {
            property("opacity", "1")
            property("transform", "none")
        }
    }

    private val cardIn by keyframes {
        from {
            property("opacity", "0")
            property("transform", "translateY(24px) scale(0.96)")
        }
        to {
            property("opacity", "1")
            property("transform", "none")
        }
    }

    private val plusPop by keyframes {
        from {
            property("opacity", "0")
            property("transform", "scale(0.2)")
        }
        30.percent {
            property("opacity", "1")
        }
        to {
            property("opacity", "1")
            property("transform", "none")
        }
    }

    private val glow by keyframes {
        from { property("box-shadow", "0 6px 18px 0 $BUTTON_SHADOW_COLOR") }
        42.percent { property("box-shadow", "0 6px 34px 4px $BUTTON_SHADOW_COLOR") }
        to { property("box-shadow", "0 6px 18px 0 $BUTTON_SHADOW_COLOR") }
    }

    /** The whole page content, as wide as the three cards. */
    val hero by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "2.22rem")
        property("max-width", "52.67rem")
        property("margin-inline", "auto")
        media(NARROW_SCREEN) {
            self style {
                property("gap", "1.56rem")
            }
        }
    }

    val greeting by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "0.67rem")
        property("text-align", "center")
    }

    val title by style {
        property("margin", "0")
        property("font-size", "clamp(1.8rem, 6vw, 2.6rem)")
        property("line-height", "1.2")
    }

    val subtitle by style {
        property("max-width", "37.78rem")
        property("margin", "0")
        property("font-size", "1.2rem")
        media(NARROW_SCREEN) {
            self style {
                property("font-size", "1rem")
            }
        }
    }

    /** Three equal columns, stacked on phones. */
    val highlights by style {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "1.33rem")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        media(NARROW_SCREEN) {
            self style {
                property("grid-template-columns", "minmax(0, 1fr)")
                property("gap", "0.89rem")
            }
        }
    }

    /** A highlight card is one link to a Portfolio section. */
    val highlight by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "0.44rem")
        property("height", "100%")
        property("padding", "1.33rem")
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("text-decoration", "none")
        property("transition", "transform 0.2s ease-out, border-color 0.2s")
        self + ":hover" style {
            property("transform", "translateY(-2px)")
            property("border-color", cssVar(Tokens.COLOR_BRAND))
        }
    }

    /** The emoji in a round chip. */
    val highlightIcon by style {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("width", "2.44rem")
        property("height", "2.44rem")
        property("margin-bottom", "0.33rem")
        property("font-size", "1.22rem")
        property("background-color", cssVar(Tokens.COLOR_CHIP))
        property("border-radius", "50%")
    }

    val highlightTitle by style {
        property("margin", "0")
        property("font-size", "1.67rem")
        property("line-height", "1.2")
    }

    /** The years and the "+" of the first card, in the brand colour. */
    val highlightNumber by style {
        property("color", cssVar(Tokens.COLOR_BRAND))
    }

    /** The counting digit keeps one width, so the title doesn't shift while it counts. */
    val countDigit by style {
        property("display", "inline-block")
        property("min-width", "1ch")
        property("text-align", "right")
        property("font-variant-numeric", "tabular-nums")
    }

    val plus by style {
        property("display", "inline-block")
    }

    val highlightAccent by style {
        property("margin", "0")
        property("font-size", "0.83rem")
        property("font-weight", "600")
        property("line-height", "1.4")
        property("color", cssVar(Tokens.COLOR_BRAND))
    }

    val highlightText by style {
        property("margin", "0")
        property("font-size", "0.81rem")
        property("color", cssVar(Tokens.COLOR_MUTED))
    }

    /** On the hero while the reveal waits for the intro: the revealed parts stay transparent, but stay readable. */
    val revealPending by style {}

    /** On the hero while the reveal plays. */
    val revealPlaying by style {}

    /** A part of the hero that is revealed. */
    val revealItem by style {}

    /** The "Let's connect" card at the end of the hero. */
    val connect by style {}

    init {
        "html.$REVEAL_GATE_CLASS .$revealPending .$revealItem" style {
            property("opacity", "0")
        }
        ".$revealPlaying .$greeting" style {
            property("animation", "${rise16.name} 0.5s ease-out backwards")
        }
        // The cards set their own animation-delay, 150 ms apart from 0.35 s.
        ".$revealPlaying .$highlight" style {
            property("animation", "${cardIn.name} 0.6s ease-out backwards")
        }
        ".$revealPlaying .$plus" style {
            property("animation", "${plusPop.name} 0.35s cubic-bezier(0.45, 1.45, 0.8, 1) 1.35s backwards")
        }
        ".$revealPlaying .$connect" style {
            property("animation", "${rise16.name} 0.5s ease-out 1.3s backwards")
        }
        ".$revealPlaying .$connect a" style {
            property("animation", "${glow.name} 0.9s ease-in-out 1.9s")
        }
        media(REDUCED_MOTION_QUERY) {
            ".$hero *" style {
                property("animation", "none", important = true)
            }
            ".$highlight" style {
                property("transition", "none")
            }
        }
    }
}
